import React, { useReducer, useMemo, useEffect, useContext, useCallback } from 'react';
import { LoggerFactory } from '@cvent/logging/LoggerFactory';
import { StreamType, useStreamChat } from '@components/chat/shared/useStreamChat';
import { StreamChat } from 'stream-chat';

const LOG = LoggerFactory.create('StreamClientContext');

const STREAM_CLIENT_REDUCER_ACTIONS = Object.freeze({
  UPDATE_CLIENT: 'UPDATE_CLIENT',
  UPDATE_CONNECTION_STATUS: 'UPDATE_CONNECTION_STATUS',
  UPDATE_LOADING_STATE: 'UPDATE_LOADING_STATE'
});

type StreamReducerParams = {
  client?: StreamChat<StreamType>;
  streamClientError?: Error;
  keepConnectionAlive?: boolean;
  loadingStreamClient?: boolean;
};

type StreamInitialState = StreamReducerParams & {
  updateClient?: (params: StreamReducerParams) => void;
  updateLoadingState?: (params: { loadingStreamClient: boolean }) => void;
  updateConnectionStatus?: (params: { keepConnectionAlive: boolean }) => void;
};

const initialState: StreamInitialState = {
  client: null,
  streamClientError: null,
  keepConnectionAlive: false,
  loadingStreamClient: false,
  updateConnectionStatus: undefined
};

const reducer = (state, action) => {
  switch (action.type) {
    case STREAM_CLIENT_REDUCER_ACTIONS.UPDATE_CLIENT: {
      const { client, streamClientError, loadingStreamClient } = action.payload;
      if (state.client && state.client.clientID === client?.clientID) {
        return state;
      }
      return {
        ...state,
        client,
        streamClientError,
        loadingStreamClient
      };
    }
    case STREAM_CLIENT_REDUCER_ACTIONS.UPDATE_CONNECTION_STATUS: {
      const { keepConnectionAlive } = action.payload;
      if (state.keepConnectionAlive === keepConnectionAlive) {
        return state;
      }
      return {
        ...state,
        keepConnectionAlive
      };
    }
    case STREAM_CLIENT_REDUCER_ACTIONS.UPDATE_LOADING_STATE: {
      const { loadingStreamClient } = action.payload;
      if (state.loadingStreamClient === loadingStreamClient) {
        return state;
      }
      return {
        ...state,
        loadingStreamClient
      };
    }
    default:
      return state;
  }
};

const StreamClientContext = React.createContext(initialState);

export interface FormattedError {
  error: Error;
  isWSFailure?: boolean;
}
const errorFormatter = (error: Error): { error: FormattedError } => {
  const isWSFailure = error?.message?.includes('"isWSFailure":true');
  return { error: { error, isWSFailure } };
};

const useStreamClient = () => {
  const {
    client,
    streamClientError,
    updateClient,
    updateLoadingState,
    keepConnectionAlive,
    loadingStreamClient,
    ...rest
  } = useContext(StreamClientContext);

  const { getStreamClient } = useStreamChat();

  const getClient = useCallback<
    (isRetry?: boolean) => Promise<{ client?: StreamChat<StreamType>; error?: FormattedError }>
  >(
    async (isRetry = false) => {
      if (client && !isRetry) {
        return { client };
      }
      if (loadingStreamClient) {
        return {};
      }
      if (streamClientError && !isRetry) {
        return errorFormatter(streamClientError);
      }
      updateLoadingState({
        loadingStreamClient: true
      });
      return new Promise<StreamChat<StreamType>>(resolve => {
        LOG.debug('Start getting StreamClient');
        const sc = getStreamClient();
        resolve(sc);
      })
        .then(client => {
          LOG.debug('StreamClient Received');
          updateClient({
            client,
            streamClientError: null,
            loadingStreamClient: false
          });
          return { client };
        })
        .catch(error => {
          updateClient({
            client: null,
            streamClientError: error,
            loadingStreamClient: false
          });
          LOG.error('An error occurred during getting StreamClient!', error);
          return errorFormatter(error);
        })
        .finally(() => {
          updateLoadingState({
            loadingStreamClient: false
          });
        });
    },
    [getStreamClient, client, updateClient, loadingStreamClient, updateLoadingState, streamClientError]
  );

  const disconnectFromStream = useCallback(
    // for some kind of error cases, we need to force disconnect from the existing stream client
    // and re-initiate that.
    (isForced = false) => {
      if (client && (!keepConnectionAlive || isForced)) {
        updateClient({
          client: null,
          streamClientError: null
        });
        client
          .disconnect()
          .then(() => LOG.debug('Disconnected from stream.'))
          .catch(error => LOG.error(`Error disconnecting from stream: ${error.message}`));
      }
    },
    [client, keepConnectionAlive, updateClient]
  );

  return {
    ...rest,
    client,
    streamClientError,
    disconnectFromStream,
    getClient,
    loadingStreamClient
  };
};

export const useDisconnectFromStreamOnUnmount = () => {
  const { disconnectFromStream } = useStreamClient();
  useEffect(
    () => () => {
      disconnectFromStream();
    },
    [disconnectFromStream]
  );
};

function StreamClientProvider({ children }: { children?: React.ReactNode }) {
  const [state, dispatch] = useReducer(reducer, {
    ...initialState
  });

  const updateClient = useCallback(
    payload => {
      dispatch({
        type: STREAM_CLIENT_REDUCER_ACTIONS.UPDATE_CLIENT,
        payload
      });
    },
    [dispatch]
  );

  const updateConnectionStatus = useCallback(
    payload => {
      dispatch({
        type: STREAM_CLIENT_REDUCER_ACTIONS.UPDATE_CONNECTION_STATUS,
        payload
      });
    },
    [dispatch]
  );

  const updateLoadingState = useCallback(
    payload => {
      dispatch({
        type: STREAM_CLIENT_REDUCER_ACTIONS.UPDATE_LOADING_STATE,
        payload
      });
    },
    [dispatch]
  );

  // Disconnect from Stream on unmount
  useEffect(
    () => () => {
      if (state.client) {
        LOG.debug('Disconnecting from stream');
        state.client.disconnect().catch(error => LOG.error('Error disconnecting from stream', error));
      }
    },
    [state.client]
  );

  const contextValue = useMemo(
    () => ({
      ...state,
      updateClient,
      updateConnectionStatus,
      updateLoadingState
    }),
    [state, updateClient, updateConnectionStatus, updateLoadingState]
  );

  return <StreamClientContext.Provider value={contextValue}>{children}</StreamClientContext.Provider>;
}

export { StreamClientContext, StreamClientProvider, useStreamClient };
